import { SupabaseClient } from "@supabase/supabase-js"

// Reward tier thresholds based on lifetime points
export const REWARD_TIERS = {
  BRONZE: { min: 0, name: "Bronze", multiplier: 1.0 },
  SILVER: { min: 1000, name: "Silver", multiplier: 1.1 },
  GOLD: { min: 5000, name: "Gold", multiplier: 1.25 },
  PLATINUM: { min: 15000, name: "Platinum", multiplier: 1.5 },
} as const

export type TierName = keyof typeof REWARD_TIERS

// Points earning rates
export const POINTS_CONFIG = {
  BASE_RATE: 1, // 1 point per $1
  // Auto-pay doubles the base rate: adds an extra +100% of base on top of the
  // tier-multiplied amount ("2× points on auto-pay"). Bronze auto-pay = 2×
  // exactly; higher tiers earn a little more, still inside the ≤1.5% real-cost
  // guardrail (see docs/rewards-brief.md).
  AUTO_PAY_BONUS: 1.0,
  REFERRAL_BONUS: 500, // Flat bonus for successful referral
  FIRST_PAYMENT_BONUS: 100, // Welcome bonus on first payment
  FIRST_SHOP_BONUS: 250, // One-time bonus for joining first shop
} as const

export const FIRST_SHOP_SOURCE = "first_shop"

// On-time rent streak milestones — a flat bonus each time a barber reaches N
// consecutive on-time months. Awarded once per milestone (idempotent by the
// `streak_<months>` history source). See awardStreakBonus.
export const STREAK_MILESTONES = [
  { months: 3, bonus: 500 },
  { months: 6, bonus: 1000 },
  { months: 12, bonus: 2500 },
] as const

export const STREAK_SOURCE_PREFIX = "streak_"

export interface PointsResult {
  success: boolean
  pointsAwarded: number
  newBalance: number
  tier: TierName
  error?: string
}

/**
 * Get user's current tier based on lifetime points
 */
export function getTierFromPoints(lifetimePoints: number): TierName {
  if (lifetimePoints >= REWARD_TIERS.PLATINUM.min) return "PLATINUM"
  if (lifetimePoints >= REWARD_TIERS.GOLD.min) return "GOLD"
  if (lifetimePoints >= REWARD_TIERS.SILVER.min) return "SILVER"
  return "BRONZE"
}

/**
 * Get tier info including name and multiplier
 */
export function getTierInfo(tier: TierName) {
  return REWARD_TIERS[tier]
}

/**
 * Calculate points with tier multiplier
 */
export function calculatePointsWithTier(basePoints: number, lifetimePoints: number): number {
  const tier = getTierFromPoints(lifetimePoints)
  const multiplier = REWARD_TIERS[tier].multiplier
  return Math.floor(basePoints * multiplier)
}

/**
 * Award points to a barber - handles all the logic atomically
 * Uses Supabase admin client to bypass RLS
 */
export async function awardPoints(
  supabaseAdmin: SupabaseClient,
  barberId: string,
  basePoints: number,
  source: string,
  description: string,
  transactionId?: string,
  options?: {
    includeAutoPayBonus?: boolean
    includeFirstPaymentBonus?: boolean
  }
): Promise<PointsResult> {
  try {
    // Get current points record
    const { data: existingPoints, error: fetchError } = await supabaseAdmin
      .from("rewards_points")
      .select("points, lifetime_points")
      .eq("barber_id", barberId)
      .single()

    const currentLifetime = existingPoints?.lifetime_points || 0

    // Calculate tier multiplier
    const tier = getTierFromPoints(currentLifetime)
    const tierMultiplier = REWARD_TIERS[tier].multiplier

    // Apply tier multiplier to base points
    let totalPoints = Math.floor(basePoints * tierMultiplier)

    // Add bonuses if applicable. Auto-pay adds another full 1× of base
    // (AUTO_PAY_BONUS = 1.0) on top of the tier-multiplied amount → "2× points".
    if (options?.includeAutoPayBonus) {
      totalPoints += Math.floor(basePoints * POINTS_CONFIG.AUTO_PAY_BONUS)
    }

    if (options?.includeFirstPaymentBonus && !existingPoints) {
      totalPoints += POINTS_CONFIG.FIRST_PAYMENT_BONUS
    }

    let newBalance: number
    let newLifetime: number

    if (existingPoints) {
      // Update existing record
      newBalance = existingPoints.points + totalPoints
      newLifetime = existingPoints.lifetime_points + totalPoints

      const { error: updateError } = await supabaseAdmin
        .from("rewards_points")
        .update({
          points: newBalance,
          lifetime_points: newLifetime,
          updated_at: new Date().toISOString(),
        })
        .eq("barber_id", barberId)

      if (updateError) throw updateError
    } else {
      // Create new record
      newBalance = totalPoints
      newLifetime = totalPoints

      const { error: insertError } = await supabaseAdmin
        .from("rewards_points")
        .insert({
          barber_id: barberId,
          points: newBalance,
          lifetime_points: newLifetime,
        })

      if (insertError) throw insertError
    }

    // Record in points history
    const { error: historyError } = await supabaseAdmin
      .from("points_history")
      .insert({
        barber_id: barberId,
        points: totalPoints,
        type: "earned",
        source,
        description,
        transaction_id: transactionId || null,
      })

    if (historyError) {
      console.error("[v0] Points history insert failed:", historyError)
      // Non-blocking - points were already awarded
    }

    // Check if tier changed
    const newTier = getTierFromPoints(newLifetime)

    console.log(`[v0] Awarded ${totalPoints} points to barber ${barberId} (tier: ${newTier})`)

    return {
      success: true,
      pointsAwarded: totalPoints,
      newBalance,
      tier: newTier,
    }
  } catch (error: any) {
    console.error("[v0] Failed to award points:", error)
    return {
      success: false,
      pointsAwarded: 0,
      newBalance: 0,
      tier: "BRONZE",
      error: error.message,
    }
  }
}

/**
 * Deduct points for reward redemption.
 * If idempotencyKey is provided and a redemption with the same
 * (barber_id, idempotency_key) already exists, returns the prior
 * balance without deducting again.
 */
export async function deductPoints(
  supabaseAdmin: SupabaseClient,
  barberId: string,
  points: number,
  rewardId: string,
  rewardName: string,
  promoCode: string,
  idempotencyKey?: string | null
): Promise<{ success: boolean; newBalance: number; error?: string; duplicate?: boolean }> {
  try {
    // Idempotency short-circuit: replay protection for client retries.
    if (idempotencyKey) {
      const { data: existing } = await supabaseAdmin
        .from("redeemed_rewards")
        .select("id")
        .eq("barber_id", barberId)
        .eq("idempotency_key", idempotencyKey)
        .maybeSingle()

      if (existing) {
        const { data: currentPoints } = await supabaseAdmin
          .from("rewards_points")
          .select("points")
          .eq("barber_id", barberId)
          .single()

        return {
          success: true,
          newBalance: currentPoints?.points ?? 0,
          duplicate: true,
        }
      }
    }

    // Get current balance
    const { data: currentPoints, error: fetchError } = await supabaseAdmin
      .from("rewards_points")
      .select("points")
      .eq("barber_id", barberId)
      .single()

    if (fetchError || !currentPoints) {
      return { success: false, newBalance: 0, error: "Points record not found" }
    }

    if (currentPoints.points < points) {
      return { success: false, newBalance: currentPoints.points, error: "Insufficient points" }
    }

    // Insert the redemption row FIRST. The unique index on
    // (barber_id, idempotency_key) is the single source of truth
    // for "did this redemption already happen?". A concurrent retry
    // racing past the lookup above will fail here with 23505.
    const { error: redeemInsertError } = await supabaseAdmin
      .from("redeemed_rewards")
      .insert({
        barber_id: barberId,
        reward_id: rewardId,
        points_spent: points,
        promo_code: promoCode,
        idempotency_key: idempotencyKey ?? null,
      })

    if (redeemInsertError) {
      if ((redeemInsertError as { code?: string }).code === "23505") {
        // Lost the race to another concurrent retry; treat as success.
        const { data: balanceAfter } = await supabaseAdmin
          .from("rewards_points")
          .select("points")
          .eq("barber_id", barberId)
          .single()
        return {
          success: true,
          newBalance: balanceAfter?.points ?? currentPoints.points,
          duplicate: true,
        }
      }
      throw redeemInsertError
    }

    const newBalance = currentPoints.points - points

    // Update points
    const { error: updateError } = await supabaseAdmin
      .from("rewards_points")
      .update({
        points: newBalance,
        updated_at: new Date().toISOString(),
      })
      .eq("barber_id", barberId)

    if (updateError) throw updateError

    // Record in history
    await supabaseAdmin.from("points_history").insert({
      barber_id: barberId,
      points: -points,
      type: "redeemed",
      source: "reward_redemption",
      description: `Redeemed: ${rewardName}`,
    })

    console.log(`[v0] Deducted ${points} points from barber ${barberId} for ${rewardName}`)

    return { success: true, newBalance }
  } catch (error: any) {
    console.error("[v0] Failed to deduct points:", error)
    return { success: false, newBalance: 0, error: error.message }
  }
}

/**
 * Award one-time bonus to a barber the first time they join a shop.
 * Idempotent — checks points_history for an existing first_shop row before awarding.
 */
export async function awardFirstShopBonus(
  supabaseAdmin: SupabaseClient,
  barberId: string
): Promise<PointsResult | null> {
  const { data: existing } = await supabaseAdmin
    .from("points_history")
    .select("id")
    .eq("barber_id", barberId)
    .eq("source", FIRST_SHOP_SOURCE)
    .limit(1)
    .maybeSingle()

  if (existing) return null

  return awardPoints(
    supabaseAdmin,
    barberId,
    POINTS_CONFIG.FIRST_SHOP_BONUS,
    FIRST_SHOP_SOURCE,
    "First shop bonus — welcome to BOOTH"
  )
}

/**
 * Award an on-time rent streak bonus when a barber reaches a milestone
 * (3 / 6 / 12 consecutive on-time months). Idempotent — each milestone is
 * awarded at most once, keyed by the `streak_<months>` history source, so a
 * caller (payment webhook or a streak-check cron) can call this safely on every
 * on-time payment. Returns null if `streakMonths` isn't a milestone or the
 * bonus was already granted.
 */
export async function awardStreakBonus(
  supabaseAdmin: SupabaseClient,
  barberId: string,
  streakMonths: number
): Promise<PointsResult | null> {
  const milestone = STREAK_MILESTONES.find((m) => m.months === streakMonths)
  if (!milestone) return null

  const source = `${STREAK_SOURCE_PREFIX}${streakMonths}`

  const { data: existing } = await supabaseAdmin
    .from("points_history")
    .select("id")
    .eq("barber_id", barberId)
    .eq("source", source)
    .limit(1)
    .maybeSingle()

  if (existing) return null

  return awardPoints(
    supabaseAdmin,
    barberId,
    milestone.bonus,
    source,
    `${streakMonths}-month on-time streak bonus`
  )
}

/**
 * Check a barber's rent-payment history and award any streak milestone they've
 * reached but not yet been paid for. Call after each successful rent payment.
 *
 * "Months on BOOTH" is proxied by the count of rent payments that earned points
 * (sources `booth_rental` + `booth_rental_renewal`). Auto-pay subscriptions bill
 * monthly, so for the common case this equals consecutive on-time months. Each
 * milestone is awarded at most once (awardStreakBonus is idempotent), so this is
 * safe to call on every payment.
 */
export async function awardEligibleStreakBonuses(
  supabaseAdmin: SupabaseClient,
  barberId: string
): Promise<void> {
  const { count } = await supabaseAdmin
    .from("points_history")
    .select("id", { count: "exact", head: true })
    .eq("barber_id", barberId)
    .in("source", ["booth_rental", "booth_rental_renewal"])

  const monthsPaid = count ?? 0

  for (const milestone of STREAK_MILESTONES) {
    if (monthsPaid >= milestone.months) {
      await awardStreakBonus(supabaseAdmin, barberId, milestone.months)
    }
  }
}

/**
 * Award referral bonus to both referrer and referee
 */
export async function awardReferralBonus(
  supabaseAdmin: SupabaseClient,
  referrerId: string,
  refereeId: string,
  refereeFirstPaymentAmount: number
): Promise<{ referrerResult: PointsResult; refereeResult: PointsResult }> {
  // Award flat bonus to referrer
  const referrerResult = await awardPoints(
    supabaseAdmin,
    referrerId,
    POINTS_CONFIG.REFERRAL_BONUS,
    "referral",
    `Referral bonus - new barber signed up`
  )

  // Award bonus to referee (10% of first payment value as bonus)
  const refereeBonusPoints = Math.floor(refereeFirstPaymentAmount * 0.1)
  const refereeResult = await awardPoints(
    supabaseAdmin,
    refereeId,
    refereeBonusPoints,
    "referral_welcome",
    `Welcome bonus from referral`
  )

  return { referrerResult, refereeResult }
}

/**
 * Fire the referral reward when a referred barber pays their first booth rent.
 *
 * The qualifying event is the first rent payment (not signup) — the fraud-
 * resistant standard for a paid product. Attribution lives in the `referrals`
 * table (a pending row is written at signup). This "claims" that row by flipping
 * pending -> rewarded in a single conditional UPDATE, so it's idempotent and
 * safe to call on every payment: only the first call that finds a pending row
 * awards points. No pending referral (or already rewarded) -> no-op.
 */
export async function awardEligibleReferralBonus(
  supabaseAdmin: SupabaseClient,
  barberId: string,
  firstPaymentAmount: number
): Promise<void> {
  // Atomically claim the pending referral. The status guard means a concurrent
  // or repeated webhook can't double-award — only one UPDATE flips it.
  const { data: claimed } = await supabaseAdmin
    .from("referrals")
    .update({
      status: "rewarded",
      rewarded_at: new Date().toISOString(),
      points_awarded: POINTS_CONFIG.REFERRAL_BONUS,
    })
    .eq("referred_id", barberId)
    .eq("status", "pending")
    .select("id, referrer_id")
    .maybeSingle()

  if (!claimed?.referrer_id) return

  // Name of the barber who was referred, for the referrer's history row.
  const { data: referred } = await supabaseAdmin
    .from("profiles")
    .select("full_name")
    .eq("id", barberId)
    .maybeSingle()
  const referredName = referred?.full_name?.trim() || "a new barber"

  // Referrer: flat referral bonus.
  await awardPoints(
    supabaseAdmin,
    claimed.referrer_id,
    POINTS_CONFIG.REFERRAL_BONUS,
    "referral",
    `Referral bonus — ${referredName} joined BOOTH`
  )

  // Referee: welcome bonus (10% of their first payment value).
  const refereeBonus = Math.floor(firstPaymentAmount * 0.1)
  if (refereeBonus > 0) {
    await awardPoints(
      supabaseAdmin,
      barberId,
      refereeBonus,
      "referral_welcome",
      "Welcome bonus from referral"
    )
  }
}

/**
 * Get barber's points summary
 */
export async function getPointsSummary(
  supabase: SupabaseClient,
  barberId: string
): Promise<{
  currentPoints: number
  lifetimePoints: number
  tier: TierName
  tierInfo: (typeof REWARD_TIERS)[TierName]
  nextTier: TierName | null
  pointsToNextTier: number
} | null> {
  const { data, error } = await supabase
    .from("rewards_points")
    .select("points, lifetime_points")
    .eq("barber_id", barberId)
    .single()

  if (error || !data) {
    return null
  }

  const tier = getTierFromPoints(data.lifetime_points)
  const tierInfo = getTierInfo(tier)

  // Calculate next tier
  let nextTier: TierName | null = null
  let pointsToNextTier = 0

  if (tier === "BRONZE") {
    nextTier = "SILVER"
    pointsToNextTier = REWARD_TIERS.SILVER.min - data.lifetime_points
  } else if (tier === "SILVER") {
    nextTier = "GOLD"
    pointsToNextTier = REWARD_TIERS.GOLD.min - data.lifetime_points
  } else if (tier === "GOLD") {
    nextTier = "PLATINUM"
    pointsToNextTier = REWARD_TIERS.PLATINUM.min - data.lifetime_points
  }

  return {
    currentPoints: data.points,
    lifetimePoints: data.lifetime_points,
    tier,
    tierInfo,
    nextTier,
    pointsToNextTier: Math.max(0, pointsToNextTier),
  }
}
